import { useMemo, useContext } from 'react';
import { FieldTypeContext } from '../../../../../providers/FieldTypeContext';
import { UserContext } from '../../../../../providers/UserContext';
import { useFormTypes } from '../../../../../use/useFormTypes';
import { useGetEntityGroups } from '../../../../../use/useGetEntityGroups';
import { formVersionsConfig } from '../../../docList/configs/formVersionsConfig';
import { homePageConfig } from '#types';
import { UIElementType } from '#types';
import type { UIElement } from '#types';
import type { FormVersionWithPath } from '#types';
import type { ColumnSpec } from '../../../docList/DocList.types';
import type { DocListConfig } from '../../../docList/DocList.types';

export const useHomeFormVersionsDynamicColumns = () => {
  const { fieldTypes } = useContext(FieldTypeContext);
  const { users } = useContext(UserContext);
  const formTypes = useFormTypes();
  const { entities } = useGetEntityGroups();

  const entityOrGroupLabelById = useMemo(() => {
    const map = new Map<string, string>();
    if (entities) {
      entities.forEach(entity => {
        const entityDocId = entity.docId;
        if (!entityDocId) return;
        
        map.set(entityDocId, entity.description?.shortLabel || entityDocId);
        
        if (entity.recordGroups) {
          Object.entries(entity.recordGroups).forEach(([groupId, group]) => {
            map.set(groupId, group.description?.shortLabel || groupId);
          });
        }
      });
    }
    return map;
  }, [entities]);

  const dynamicColumns = useMemo(() => {
    const formTypeData = formTypes.data;
    if (!formTypeData || formTypeData.length === 0) {
      return [];
    }

    const sortableFieldIdSet = new Set(homePageConfig.formsTable.sortableFieldIds ?? []);
    const fieldIdSet = new Set<string>();
    const entityRefByColumnId = new Map<
      string,
      { columnId: string; header: string; entityOrGroupId: string }
    >();

    const visit = (element: UIElement): void => {
      switch (element.uiElementType) {
        case UIElementType.Field: {
          fieldIdSet.add(element.fieldId);
          break;
        }
        case UIElementType.EntityReference: {
          const entityId = element.entityId;
          if (!entityId) break;

          const groupId = element.entityGroupId;
          const isGrouped = Boolean(groupId);
          const columnId = isGrouped ? `group_${entityId}_${groupId}` : `entity_${entityId}`;
          const entityOrGroupId = groupId || entityId;

          const excludeKeys = homePageConfig.formsTable.excludeDynamicEntityReferenceKeys ??
            (homePageConfig.formsTable.primaryEntityReferenceKey ? [homePageConfig.formsTable.primaryEntityReferenceKey] : []);
          
          if (excludeKeys.includes(entityOrGroupId)) {
            break;
          }

          if (!entityRefByColumnId.has(columnId)) {
            const label = groupId
              ? entityOrGroupLabelById.get(groupId) || groupId
              : entityOrGroupLabelById.get(entityId) || entityId;

            entityRefByColumnId.set(columnId, {
              columnId,
              header: label,
              entityOrGroupId,
            });
          }
          break;
        }
      }

      if ('children' in element) {
        element.children.forEach(visit);
      }
    };

    formTypeData.forEach((formType) => {
      visit(formType.defaultUILayout.structure);
    });

    const fieldColumns: ColumnSpec<FormVersionWithPath>[] = Array.from(fieldIdSet)
      .sort((a, b) => {
        const aLabel = fieldTypes[a]?.description?.shortLabel || a;
        const bLabel = fieldTypes[b]?.description?.shortLabel || b;
        return aLabel.localeCompare(bLabel);
      })
      .flatMap((fieldId) => {
        const fieldType = fieldTypes[fieldId];

        return [{
          id: `field_${fieldId}`,
          header: fieldType?.description?.shortLabel || fieldId,
          accessor: (row: FormVersionWithPath) => row.fields[fieldId]?.value,
          filterable: true,
          size: 180,
          ...(sortableFieldIdSet.has(fieldId)
            ? { serverSortField: `fields.${fieldId}.value` }
            : {}),
        }];
      });

    const entityRefColumns: ColumnSpec<FormVersionWithPath>[] = Array.from(entityRefByColumnId.values())
      .sort((a, b) => a.header.localeCompare(b.header))
      .map((spec) => ({
        id: spec.columnId,
        header: spec.header,
        accessor: (row) => row.entityReferences?.[spec.entityOrGroupId]?.entityRecordName,
        filterable: true,
        size: 180,
      }));

    return [...fieldColumns, ...entityRefColumns];
  }, [entityOrGroupLabelById, fieldTypes, formTypes.data]);

  const primaryEntityColumn = useMemo(() => {
    const primaryKey = homePageConfig.formsTable.primaryEntityReferenceKey;
    if (!primaryKey) {
      return null;
    }

    const label = homePageConfig.formsTable.primaryEntityColumnLabel ?? 
      entityOrGroupLabelById.get(primaryKey) ?? primaryKey;

    return {
      id: primaryKey,
      header: label,
      accessor: (row: FormVersionWithPath) => row.entityReferences?.[primaryKey]?.entityRecordName ?? '',
      filterable: true,
      serverFilterField: `entityReferences.${primaryKey}.entityRecordName`,
      serverSortField: `entityReferences.${primaryKey}.entityRecordName`,
      size: 300,
    } as ColumnSpec<FormVersionWithPath>;
  }, [entityOrGroupLabelById]);

  const configWithDynamicColumns = useMemo<DocListConfig<FormVersionWithPath>>(() => {
    const userFilterOptions = () => users.map(u => ({ label: (u as any).name || (u as any).email || u.docId || '', value: u.docId || '' }));

    const runtimeFilterOptions: Record<string, ColumnSpec<FormVersionWithPath>['getFilterOptions']> = {
      formType: () => (formTypes.data ?? []).map(ft => ({ label: ft.description?.shortLabel || ft.docId || '', value: ft.docId || '' })),
      assignedTo: userFilterOptions,
      subscribed: userFilterOptions,
      modifiedBy: userFilterOptions,
    };

    const baseColumns = formVersionsConfig.columns.map((col) => (
      runtimeFilterOptions[col.id] ? { ...col, getFilterOptions: runtimeFilterOptions[col.id] } : col
    ));
    const columns = primaryEntityColumn
      ? [primaryEntityColumn, ...baseColumns, ...dynamicColumns]
      : [...baseColumns, ...dynamicColumns];

    return {
      ...formVersionsConfig,
      columns,
    };
  }, [dynamicColumns, primaryEntityColumn, formTypes.data, users]);

  return configWithDynamicColumns;
};


